import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  authHeader,
  errMessage,
  getToken,
  setToken,
} from "../api";
import { fmtWhen, relative } from "../lib/dates";
import { useBodyClass, useDocumentTitle } from "../components/Chrome";
import GatheringsPanel from "../components/admin/GatheringsPanel";
import CirclesPanel from "../components/admin/CirclesPanel";
import InquiriesPanel from "../components/admin/InquiriesPanel";
import SubscribersPanel from "../components/admin/SubscribersPanel";
import WiresPanel from "../components/admin/WiresPanel";
import CopyPanel from "../components/admin/CopyPanel";

/* ------------------------------------------------------------------ gate */
function Gate({ onIn }) {
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!pass.trim()) {
      setErr("This needs an answer.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { passphrase: pass });
      setToken(data.token);
      onIn();
    } catch (e2) {
      setErr(errMessage(e2, "That passphrase doesn't open this door."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate" data-testid="admin-gate">
      <div className="inner">
        <div className="glogo" aria-hidden="true" />
        <p className="eyebrow sc" style={{ justifyContent: "center" }}>
          <span className="num">&middot;</span>
          <span>The back room</span>
        </p>
        <h1>Who&rsquo;s there?</h1>
        <p>
          One passphrase, no username. This is where the gatherings, the circles
          and the inbox live.
        </p>
        <form onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="pass">Passphrase</label>
            <input
              id="pass"
              type="password"
              value={pass}
              autoComplete="current-password"
              autoFocus
              onChange={(e) => {
                setPass(e.target.value);
                setErr("");
              }}
              aria-invalid={err ? "true" : "false"}
              data-testid="admin-passphrase-input"
            />
            {err ? (
              <span className="err" data-testid="admin-login-error">
                <span className="lab">Note</span>
                {err}
              </span>
            ) : null}
          </div>
          <button
            type="submit"
            className="send"
            disabled={busy}
            style={{ width: "100%" }}
            data-testid="admin-login-button"
          >
            {busy ? "Checking\u2026" : "Come in"}
          </button>
        </form>
        <p style={{ marginTop: "2rem", fontSize: ".9rem" }}>
          <Link to="/" className="linkish">
            Back to the front door
          </Link>
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- overview */
function Overview({ stats, onGo }) {
  if (!stats) return <p className="admin-help">Looking&hellip;</p>;
  const next = stats.gatherings?.next;

  return (
    <section className="admin-sec" data-testid="admin-overview-panel">
      <header>
        <h2>Where things stand</h2>
      </header>
      <p className="admin-help">
        A quick look at the front door. Everything people send you is kept on this
        site, so you can always come here and find it.
      </p>

      <div className="stats" data-testid="admin-stats">
        <div className="stat">
          <span className="k">Coming up</span>
          <span className="v">{stats.gatherings?.upcoming ?? 0}</span>
          <span className="sub">
            {stats.gatherings?.drafts
              ? `${stats.gatherings.drafts} not published yet`
              : "gatherings on the calendar"}
          </span>
        </div>
        <div className="stat">
          <span className="k">People coming</span>
          <span className="v">{stats.rsvps?.confirmed ?? 0}</span>
          <span className="sub">
            {stats.rsvps?.waitlist
              ? `${stats.rsvps.waitlist} on waitlists`
              : "across every gathering"}
          </span>
        </div>
        <div className="stat">
          <span className="k">New notes</span>
          <span className="v">{stats.inquiries?.new ?? 0}</span>
          <span className="sub">{stats.inquiries?.total ?? 0} in all</span>
        </div>
        <div className="stat">
          <span className="k">On the list</span>
          <span className="v">{stats.subscribers?.active ?? 0}</span>
          <span className="sub">want to hear when something is on</span>
        </div>
      </div>

      {next ? (
        <div className="panel" data-testid="admin-next-gathering">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>The next thing</span>
          </p>
          <h3>{next.title}</h3>
          <p>
            {fmtWhen(next.starts_at, next.ends_at)} &middot; {relative(next.starts_at)}
            {next.venue ? ` \u00b7 ${next.venue}` : ""}
            <br />
            {next.capacity
              ? `${next.seats_taken} of ${next.capacity} seats taken.`
              : "No seat limit."}
          </p>
          <button
            type="button"
            className="act"
            onClick={() => onGo("gatherings")}
            data-testid="admin-overview-gatherings-button"
          >
            See who&rsquo;s coming &rarr;
          </button>
        </div>
      ) : (
        <div className="empty" data-testid="admin-overview-empty">
          <h3 style={{ marginBottom: ".8rem" }}>Nothing on the calendar.</h3>
          <p>
            The public page is honest about it rather than looking broken &mdash; it
            invites people onto the list instead. Add a date whenever you&rsquo;re
            ready.
          </p>
          <p style={{ marginTop: "1.4rem" }}>
            <button
              type="button"
              className="linkish"
              onClick={() => onGo("gatherings")}
              data-testid="admin-overview-create-button"
            >
              Make a gathering &rarr;
            </button>
          </p>
        </div>
      )}

      {!stats.integrations?.email?.configured ? (
        <div className="notice" data-testid="admin-overview-email-warning">
          Email notifications are off, so you won&rsquo;t be told when something
          arrives &mdash; you&rsquo;ll need to check this dashboard. Nothing is
          ever lost either way.{" "}
          <button type="button" className="linkish" onClick={() => onGo("wires")}>
            How to switch it on
          </button>
        </div>
      ) : null}
    </section>
  );
}

/* ----------------------------------------------------------------- shell */
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "gatherings", label: "Gatherings" },
  { key: "circles", label: "Circles" },
  { key: "inbox", label: "Inbox" },
  { key: "list", label: "The list" },
  { key: "words", label: "Words" },
  { key: "wires", label: "Wires" },
];

export default function Admin() {
  useBodyClass("admin");
  useDocumentTitle("The back room");

  const [authed, setAuthed] = useState(null); // null = checking
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [stats, setStats] = useState(null);
  const [gatherings, setGatherings] = useState([]);
  const [circles, setCircles] = useState([]);
  const [interest, setInterest] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  /* ---- session check on mount */
  useEffect(() => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    api
      .get("/admin/me", { headers: authHeader() })
      .then(() => setAuthed(true))
      .catch(() => {
        setToken("");
        setAuthed(false);
      });
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const h = { headers: authHeader() };
      const [o, g, c, ci, i, s] = await Promise.all([
        api.get("/admin/overview", h),
        api.get("/admin/gatherings", h),
        api.get("/admin/circles", h),
        api.get("/admin/circle-interest", h),
        api.get("/admin/inquiries", h),
        api.get("/admin/subscribers", h),
      ]);
      setStats(o.data);
      setGatherings(g.data.gatherings || []);
      setCircles(c.data.circles || []);
      setInterest(ci.data.interest || []);
      setInquiries(i.data.inquiries || []);
      setSubscribers(s.data.subscribers || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        setToken("");
        setAuthed(false);
      } else {
        setErr(errMessage(e, "We couldn't load the dashboard."));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) loadAll();
  }, [authed, loadAll]);

  const signOut = async () => {
    try {
      await api.post("/admin/logout", null, { headers: authHeader() });
    } catch (e) {
      // The session is going away locally regardless, so this never blocks
      // sign-out — but a failure here means a token is still live server-side.
      console.warn("Sign-out request failed; clearing the session locally.", e);
    }
    setToken("");
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="loading">Checking&hellip;</div>;
  }

  if (!authed) {
    return <Gate onIn={() => setAuthed(true)} />;
  }

  const newCount = inquiries.filter((i) => i.status === "new").length;

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <div className="admin-top">
        <Link to="/" className="amark">
          Sch&#275;ma
        </Link>
        <span className="who">The back room</span>
        <button
          type="button"
          className="linkish"
          onClick={loadAll}
          data-testid="admin-refresh-button"
        >
          Refresh
        </button>
        <button
          type="button"
          className="linkish"
          onClick={signOut}
          data-testid="admin-signout-button"
        >
          Sign out
        </button>
      </div>

      <nav className="admin-nav" data-testid="admin-nav">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            aria-current={tab === t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
          >
            {t.label}
            {t.key === "inbox" && newCount ? (
              <span className="count">{newCount}</span>
            ) : null}
          </button>
        ))}
      </nav>

      {err ? (
        <div className="form-error" data-testid="admin-load-error">
          {err}{" "}
          <button type="button" className="linkish" onClick={loadAll}>
            Try again
          </button>
        </div>
      ) : null}

      {tab === "overview" ? <Overview stats={stats} onGo={setTab} /> : null}
      {tab === "gatherings" ? (
        <GatheringsPanel rows={gatherings} reload={loadAll} loading={loading} />
      ) : null}
      {tab === "circles" ? (
        <CirclesPanel
          rows={circles}
          interest={interest}
          reload={loadAll}
          loading={loading}
        />
      ) : null}
      {tab === "inbox" ? (
        <InquiriesPanel rows={inquiries} reload={loadAll} loading={loading} />
      ) : null}
      {tab === "list" ? (
        <SubscribersPanel
          rows={subscribers}
          integrations={stats?.integrations}
          reload={loadAll}
          loading={loading}
        />
      ) : null}
      {tab === "words" ? <CopyPanel /> : null}
      {tab === "wires" ? <WiresPanel integrations={stats?.integrations} /> : null}
    </div>
  );
}
